import { useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import { useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'

import { OtpPanel } from '@/customer/components/auth/otp-panel'
import {
  VERIFICATION_SUCCESS_HOLD_MS,
  VerificationSuccessPanel,
} from '@/customer/components/auth/verification-success-panel'
import { PageContainer } from '@/customer/components/page-container'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import { meQueryKey, useGetMe } from '@/customer/hooks/use-get-me'
import { authClient } from '@/customer/lib/auth'
import { routes } from '@/customer/routes'

/**
 * Verify an existing member's phone without changing it. Common
 * signup path: the member enters a phone during registration but
 * doesn't complete verification until later in onboarding.
 *
 * Auto-dispatches an OTP on mount, then verifies against
 * `phoneNumber.verify` with `disableSession: true` so we don't
 * rotate the already-live session cookie. `updatePhoneNumber` is
 * omitted - we only want to prove ownership of the current number,
 * not swap it. Value swaps live on `/phone/update`.
 */
export function PhoneVerificationPage() {
  const homeUrl = routes.account.index
  const updatePhoneUrl = routes.account.phone.update
  useDocumentTitle('Verify your phone')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const me = useGetMe()
  const [isSuccess, setIsSuccess] = useState(false)

  if (!me.data) return <PageContainer backLink={{ to: homeUrl, label: 'Back to account' }} />

  const { phone, phoneVerified } = me.data.member
  if (!phone) return <Navigate to={homeUrl} replace />
  if (phoneVerified && !isSuccess) return <Navigate to={homeUrl} replace />

  function handleVerified() {
    setIsSuccess(true)
    queryClient.invalidateQueries({ queryKey: meQueryKey })
    window.setTimeout(() => navigate(homeUrl), VERIFICATION_SUCCESS_HOLD_MS)
  }

  return (
    <PageContainer
      backLink={{ to: homeUrl, label: 'Back to account' }}
      headline={[
        { text: 'Verify your', color: 'yellow' },
        { text: 'phone', color: 'blue' },
      ]}
    >
      <AnimatePresence mode="wait">
        {isSuccess ? (
          <motion.div key="success">
            <VerificationSuccessPanel active={isSuccess} />
          </motion.div>
        ) : (
          <motion.div
            key="otp"
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex flex-col gap-4"
          >
            <OtpPanel
              autoSend
              onSuccess={handleVerified}
              sendCode={async () => {
                const { data, error } = await authClient.phoneNumber.sendOtp({
                  phoneNumber: phone,
                })
                if (error) throw error
                if (!data) throw new Error('Missing data')
              }}
              verifyCode={async (code) => {
                const { error } = await authClient.phoneNumber.verify({
                  phoneNumber: phone,
                  code,
                  disableSession: true,
                })
                if (error) throw error
              }}
              blurb={
                <p className="text-body-md text-foreground">
                  We&apos;ve sent a 6-digit code to <span className="font-semibold">{phone}</span>{' '}
                  via SMS. Enter it below to confirm your number.
                </p>
              }
              extras={
                <div className="flex items-center gap-2 text-label-sm text-muted-foreground">
                  <span>Not your number?</span>
                  <Link
                    to={updatePhoneUrl}
                    className="font-semibold text-foreground underline underline-offset-4"
                  >
                    Update it
                  </Link>
                </div>
              }
            />
          </motion.div>
        )}
      </AnimatePresence>
    </PageContainer>
  )
}
